import { onMounted, reactive } from 'vue';
import { userStore } from '@/store/user-store';
import { IUser } from '@/models/user.model';
import { onBeforeMount } from 'vue';
import { RepositoryFactory } from '@/api/repositoryFactory';

export default function useAuthentication() {
  const AuthRepository = RepositoryFactory.get('auth');
  const authTokenName = 'wdw-login-auth-token';
  const virtualAuthTokenName = 'wdw-virtual-auth-token';

  // set TTL in seconds
  function setItemWithExpiry(key: string, value: any, ttl: number) {
    if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
      // do not use auth-token in local storage on internal use
      return;
    }
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + ttl * 1000, // getTime is milliseconds
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  function getItemWithExpiry(key: string) {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  function initVirtualUserToken() {
    // do not use init virtual users via api-token on internal use
    if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
      return;
    }
    // console.log("checking virtual auth token in storage");
    // don't do anything if we have valid user settings already in store, or loading, or virtual user is not allowed
    if (
      !process.env.VUE_APP_ENABLE_VIRTUAL_USER ||
      userStore.getState().authToken ||
      userStore.isLoading() ||
      userStore.isLoggedIn() ||
      userStore.isVirtualUser()
    ) {
      return userStore.getState().authToken;
    }

    // check for existing token
    const guestToken = getItemWithExpiry(virtualAuthTokenName);
    if (guestToken) {
      userStore.setVirtualAuthToken(guestToken);
      return guestToken;
    }
  }

  function initStorageAuthToken(unsetIfNoToken = true) {
    // console.log("checking login auth token in storage");
    const token = getItemWithExpiry(authTokenName);
    if (token) {
      userStore.setAuthToken(token);
      return token;
    } else if (unsetIfNoToken) {
      // if neither "normal" nor virtual token is available, make double sure that we have no user in store
      userStore.unsetAuthToken();
    }
  }

  function setAuthToken(token: string) {
    if (token === null || typeof token === 'undefined' || token === '') {
      localStorage.removeItem(authTokenName);
      // emit event that we logged out
      window.emitter.emit('wdw-login-logout', { loggedIn: false });
    } else {
      // set auth token for 1 day validity
      setItemWithExpiry(authTokenName, token, 3600 * 24);
      // emit event that we logged in
      window.emitter.emit('wdw-login-logout', { loggedIn: true });
    }
  }
  function setVirtualAuthToken(token: string) {
    if (token === null || typeof token === 'undefined') {
      localStorage.removeItem(virtualAuthTokenName);
      // emit event that we logged out
      window.emitter.emit('wdw-virtual-user');
    } else {
      // set virtual token for 3 years validity
      setItemWithExpiry(virtualAuthTokenName, token, 3600 * 24 * 365 * 3);
      // emit event that we logged in
      window.emitter.emit('wdw-virtual-user');
    }
  }

  function getVirtualUser() {
    return new Promise((resolve, reject) => {
      // do not use init virtual users via api-token on internal use
      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        reject('not allowed to initiate virtual user');
      }

      // don't do anything if we have valid user settings already in store, or loading
      if (
        !process.env.VUE_APP_ENABLE_VIRTUAL_USER ||
        userStore.getState().authToken ||
        userStore.isLoading() ||
        userStore.isLoggedIn()
      ) {
        reject(false);
      } else if (userStore.isVirtualUser()) {
        resolve(true);
      }

      // try to check if there is a token available, just not initialized yet
      const authToken = initVirtualUserToken();
      if (authToken) {
        resolve(true);
      }

      // else get new virtual user from server
      AuthRepository.getVirtualUser()
        .then((response: any) => {
          if (response && response.data.success && response.data.data && response.data.data.token) {
            setVirtualAuthToken(response.data.data.token);
            resolve(true);
          }
          reject(false);
        })
        .catch((error: any) => {
          console.error(error.response);
          reject(error);
        });
    });
  }

  // global window EVENT
  window.emitter.on('wdw-login-logout', e => {
    // console.info('wdw-login-logut event');
    if (e.loggedIn) {
      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        // directly try to fetch user (internal auth via cookie)
        userStore.fetchUser(false);
      } else {
        // get auth_token from local-storage and fetch user object on login
        initStorageAuthToken();
      }
    } else {
      // required for components other than Login
      // unset token
      userStore.unsetAuthToken();
      // try to see if we have a virtual token to fall back to
      initVirtualUserToken();
      // or force virtual user to always be present?
      // getVirtualUser().then(success => {
      //     console.log('initialized virtual user after logout');
      // }).catch(e => {
      //     console.log('error getting virtual user after logout');
      // });
    }
  });
  // global window EVENT
  window.emitter.on('wdw-virtual-user', e => {
    // console.info('wdw-virtual-user event');
    // get auth_token from local-storage and fetch user object on login
    initVirtualUserToken();
  });

  // try to init token on mounted
  onMounted(() => {
    if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
      // try to fetch user (via cookie)
      userStore.fetchUser(false);
    } else {
      const unsetIfNoToken = false;
      let authToken = initStorageAuthToken(unsetIfNoToken);
      if (!authToken) {
        authToken = initVirtualUserToken();
      }
      if (!authToken) {
        // if neither "normal" nor virtual token is available, make double sure that we have no user in store
        userStore.unsetAuthToken();
      }
    }
  });

  return {
    initStorageAuthToken,
    setAuthToken,
    setVirtualAuthToken,
    getVirtualUser,
  };
}
